import React, { useState, useEffect } from 'react';
import AssessmentIcon from '@mui/icons-material/Assessment';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
  Fade,
  Grow,
  Zoom,
  alpha,
  Skeleton,
  Stack,
  Avatar,
  Fab,
} from '@mui/material';
import {
  Event as EventIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingIcon,
  ArrowForward as ArrowForwardIcon,
  Newspaper as NewspaperIcon,
  PhotoCamera as PhotoCameraIcon,
  Mic as MicIcon,
  Groups as GroupsIcon,
  Celebration as CelebrationIcon,
  Public as PublicIcon,
  School as SchoolIcon,
  Share as ShareIcon,
  Launch as LaunchIcon,
  Star as StarIcon,
  Language as LanguageIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import { motion } from 'framer-motion';

function ActualitesEtEvenements() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const primaryColor = '#D32F2F';
  const secondaryColor = '#2E7D32';
  const tertiaryColor = '#f8f9fa';
  const white = '#ffffff';
  const accentColor = '#1976d2';
  const neutralGray = '#757575';

  const [hoveredCard, setHoveredCard] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  // Catégories
  const categories = [
    { id: 0, label: 'Tous', icon: <EventIcon /> },
    { id: 7, label: 'Activités sur terrain', icon: <LanguageIcon /> },
    { id: 1, label: 'Événements', icon: <CelebrationIcon /> },
    { id: 2, label: 'Conférences', icon: <MicIcon /> },
    { id: 3, label: 'Académique', icon: <SchoolIcon /> },
    { id: 4, label: 'Photos', icon: <PhotoCameraIcon /> },
    { id: 5, label: 'Formations / Ateliers', icon: <LanguageIcon /> },
    { id: 6, label: 'Résultats Internationaux', icon: <PublicIcon /> }
  ];

  // Fonction pour les images
  const getImagePath = (fileName) => {
    if (!fileName || fileName.trim() === '') return null;
    const cleanName = fileName.trim();
    const extensions = ['.jpg', '.jpeg', '.png', '.webp', '.JPG', '.JPEG', '.PNG'];
    const hasExtension = extensions.some(ext => cleanName.endsWith(ext));
    return hasExtension ? `/assets/formation/${cleanName}` : `/assets/formation/${cleanName}.jpg`;
  };

  // ---- ÉVÉNEMENTS PRINCIPAUX ----
  const evenementsPrincipaux = [
  
    // NOUVEL ÉVÉNEMENT : DATES ET LIEUX DE FORMATION RPPCUB
    {
      id: 'dates-lieux-formation-rppcub-2026',
      title: 'Dates et lieux de formation des agents recenseurs RPPCUB',
      subtitle: 'Sessions obligatoires par commune – juillet/août 2026',
      date: '25 Juillet 2026',
      description: 'L\'INSBU publie le calendrier définitif des formations pour les candidats présélectionnés. Les sessions se tiendront du 28 au 31 juillet à Ngozi, Rumonge et Gitega ; du 3 au 7 août à Ntahangwa, Mugere et Mukaza. Les chefs d\'équipes auront des sessions spécifiques. Début impératif à 08h00. La note de service complète est disponible en téléchargement.',
      image: '/assets/rppcub/ppcub2026.png', 
      icon: <SchoolIcon />,
      color: '#008D36',
      lien: '/trainingSchedule', 
      badge: 'Formation obligatoire',
      category: 5,
      featured: true,
      stats: { participants: 'Candidats présélectionnés', duration: 'Plusieurs sessions' }
    },
      {
      id: 'lancement-rppcub-2026',
      title: 'Lancement officiel du RPPCUB 2026',
      subtitle: 'Recensement des Propriétaires des Propriétés/Parcelles dans les Centres Urbains du Burundi',
      date: '14 Juillet 2026',
      description: 'Son Excellence le Premier Ministre Nestor Ntahontuye a présidé la cérémonie de lancement des activités du RPPCUB 2026, une opération d\'envergure pour moderniser la gouvernance foncière et accélérer la transformation numérique du pays.',
      image: '/assets/rppcub/premier-ministre.jpeg',
      icon: <EventIcon />,
      color: '#008D36',
      lien: '/actualiteRPPCUB',
      badge: 'Lancement officiel',
      category: 1,
      featured: true,
      stats: { participants: 'Autorités nationales', duration: '1 journée' }
    },
    {
      id: 'publication-strategie-ssn-2026-2030',
      title: 'Publication de la Stratégie de Communication du SSN 2026-2030',
      subtitle: 'Un cap clair pour une communication statistique moderne et inclusive',
      date: '30 Juin 2026',
      description: 'L\'INSBU publie la Stratégie de Communication et de Diffusion des Statistiques du Système Statistique National (SSN) pour la période 2026-2030. Élaborée dans le cadre du projet PROSTATBU, elle vise à renforcer la visibilité, l\'accessibilité et l\'utilisation des données officielles, en alignement avec les standards de la Communauté d\'Afrique de l\'Est (EAC).',
      image: '/assets/strategie-ssn/strategie_communication_image.jpg',
      icon: <DescriptionIcon />,
      color: '#008D36',
      lien: '/publicationStrategie',
      badge: 'Document stratégique',
      category: 1,
      featured: true,
      stats: { participants: 'SSN Burundi', duration: '2026-2030' }
    },
    {
      id: 'saisie-numerique-etat-civil-2026',
      title: 'Saisie numérique des faits d’état civil 2025',
      subtitle: 'Mission nationale de suivi et de renforcement des compétences (09-16 juin 2026)',
      date: '09-16 Juin 2026',
      description: 'L’INSBU, en collaboration avec le Ministère de l’Intérieur et avec l’appui du PNUD, a organisé une mission de suivi et de formation des agents d’état civil pour la digitalisation des registres (naissances, mariages, décès) dans tout le Burundi.',
      image: '/assets/etatcivil/20260616_104436.jpg',
      icon: <GroupsIcon />,
      color: '#2E7D32',
      lien: '/saisieNumeriqueEtatCivil',
      badge: 'Opération nationale',
      category: 7,
      featured: true,
      stats: { participants: 'Agents d\'état civil', duration: '5 jours' }
    },
    {
      id: 'preselection-rppcub-2026',
      title: 'Présélection des agents recenseurs RPPCUB',
      subtitle: 'Recensement des Propriétaires des Propriétés/Parcelles dans les centres urbains',
      date: '22-26 Juin 2026',
      description: 'L\'INSBU a publié la liste des candidats présélectionnés pour la formation dans le cadre du RPPCUB. Les intéressés doivent confirmer leur disponibilité au 22216734 de 8h à 12h.',
      image: '/assets/page1.jpg',
      icon: <GroupsIcon />,
      color: '#2E7D32',
      lien: '/preselectionList',
      badge: 'Recrutement',
      category: 5,
      featured: true,
      stats: { participants: 'Candidats présélectionnés', duration: 'Confirmation avant le 26 juin' }
    },
    {
      id: 'atelier-validation-strategie-communication-ssn',
      title: 'Atelier de validation de la stratégie de communication du SSN',
      subtitle: 'Vers une communication statistique harmonisée avec l\'EAC',
      date: '08 mai 2026',
      description: 'Organisé par l\'INSBU au City Hill Hotel, Bujumbura, cet atelier a permis de valider la nouvelle stratégie de communication du Système Statistique National, en présence de multiples parties prenantes.',
      image: '/assets/validation-atelier/image6presentation.jpeg',
      icon: <GroupsIcon />,
      color: '#2E7D32',
      lien: '/atelierValidation',
      badge: 'Atelier de validation',
      category: 5,
      featured: true,
      stats: { participants: 'Multiples parties prenantes', duration: '1 journée' }
    },
    {
      id: 'formation-eicvmb-2026',
      title: 'Formation agents enquêteurs EICVMB 2025-2026',
      subtitle: 'Lancement de la collecte nationale',
      date: '04 Février 2026',
      description: 'Formation des agents pour l\'enquête sur les conditions de vie des ménages.',
      image: '/assets/formation/dg_consultante.jpeg',
      icon: <AssessmentIcon />,
      color: '#008D36',
      lien: '/formationEnqueteursPage',
      badge: 'Formation Enquêteurs',
      category: 5,
      featured: true,
      stats: { participants: 'Agents formés', duration: 'Collecte jusqu\'à février 2027' }
    },
    {
      id: 'mission-communication-ssn',
      title: 'Mission pour la stratégie de communication du SSN',
      subtitle: 'Stratégie de communication du Système Statistique National',
      date: '04 février 2026',
      description: 'Mission nationale visant à actualiser la stratégie de communication du SSN du Burundi afin d’améliorer la visibilité des statistiques.',
      image: '/assets/mission-communication/gitega.jpeg',
      icon: <AssessmentIcon />,
      color: '#008D36',
      lien: '/missionCommunicationPage',
      badge: 'Mission nationale',
      category: 7,
      featured: true,
      stats: { participants: '', duration: '1 semaine' }
    },
    {
      id: 'resultats-odin-2024',
      title: 'Résultats ODIN 2024 pour le Burundi',
      subtitle: 'Évaluation de la couverture et de l\'ouverture des données',
      date: '29 Avril 2025',
      description: 'Publication des résultats de l\'Inventaire des Données Ouvertes (ODIN) 2024 par Open Data Watch.',
      image: getImagePath("odin-results.jpg"),
      icon: <AssessmentIcon />,
      color: '#008D36',
      lien: '/odinPage',
      badge: 'Résultats Internationaux',
      category: 6,
      featured: true,
      stats: { participants: 'Open Data Watch', duration: '2 ans' }
    },
    {
      id: 'formation-inclusion',
      title: 'Formation sur l\'inclusion statistique',
      subtitle: 'Renforcement des capacités à Dakar & Yaoundé',
      date: '10-16 Décembre 2025',
      description: 'Formation sur l\'inclusion des populations déplacées de force dans les recensements et enquêtes nationales, suivie d\'un voyage d\'étude au Cameroun',
      image: getImagePath("photo_famille.png"),
      icon: <LanguageIcon />,
      color: '#2E7D32',
      lien: '/formaFormationPage',
      badge: 'Formation Internationale',
      category: 5,
      featured: true,
      stats: { participants: 'Délégation INS Burundi', duration: '7 jours' }
    },
    {
      id: 'cloture-2025',
      title: 'Cérémonie de Clôture du MSB 2025',
      subtitle: '33ème Journée Africaine de la Statistique',
      date: '19 Novembre 2025',
      description: 'Clôture solennelle à l\'Hôtel Royal Palace avec présentation des résultats et discours du Premier Ministre',
      image: '/assets/msb/Cloture_JAS_19-11-2025_Royal_palace-photo_cloture.jpg',
      icon: <EventIcon />,
      color: '#2E7D32',
      lien: '/clotureMoisStatistique2025',
      badge: 'Clôture Solennelle',
      category: 1,
      featured: true,
      stats: { participants: '200+', duration: '1 journée' }
    },
    {
      id: 'cafe-statistique',
      title: 'Café Statistique - Couplage RGPH/RGAE',
      subtitle: 'Donatus Center Conference',
      date: '18 Novembre 2025',
      description: 'Un cadre d\'échange interactif pour capitaliser sur les expériences du RGPHAE 2024, explorer les innovations statistiques et renforcer les synergies entre les différents processus de collecte de données pour une meilleure planification du développement.',
      image: '/assets/msb/Cafe_Statistique_18-11-2025_DCC-photodefamille.jpg',
      icon: <GroupsIcon />,
      color: '#2E7D32',
      lien: '/cafeStatistiqueRGPH_RGAE',
      badge: 'Café Statistique',
      category: 1,
      featured: true,
      stats: { participants: '100+', duration: '1 journée' }
    },
    {
      id: 'lancement-2025',
      title: 'Lancement du 14ème Mois de la Statistique',
      subtitle: '4ème Journée Mondiale de la Statistique',
      date: '20 Octobre 2025',
      description: 'Cérémonie officielle sous le Haut Patronage du Cabinet de la Primature avec point de presse animé par M. NDAYISENGA Modeste, DG ai de l\'INSBU',
      image: '/assets/msb/dgai_anime_presse_lancement_msb2025_celebration_jms2025.jpg',
      icon: <CelebrationIcon />,
      color: '#2E7D32',
      lien: '/lancementMoisStatistique2025',
      badge: 'Lancement Officiel',
      category: 1,
      featured: true,
      stats: { participants: '100+', duration: '1 journée' }
    },
    {
      id: 'congres-etudiants',
      title: '1er Congrès des Étudiants en Statistique',
      subtitle: 'Université du Lac Tanganyika',
      date: '06-07 Novembre 2025',
      description: 'Premier événement académique réunissant étudiants et professionnels pour échanger sur l\'avenir de la statistique au Burundi',
      image: '/assets/msb/CONGRES1.jpeg',
      icon: <GroupsIcon />,
      color: '#2E7D32',
      lien: '/premierCongresEtudiantsStatistique',
      badge: 'Première Édition',
      category: 3,
      featured: true,
      stats: { participants: '100+ étudiants', duration: '2 jours' }
    },
  ];

  // ---- ARTICLES RÉCENTS ----
  const articlesRecents = [
    {
      id: 'publication-strategie-ssn',
      title: 'Publication de la Stratégie de Communication du SSN 2026-2030',
      date: '30 Juin 2026',
      category: 'Document stratégique',
      excerpt: 'L\'INSBU dévoile la nouvelle stratégie de communication du Système Statistique National, alignée sur les standards de l\'EAC et visant à renforcer l\'accès et l\'utilisation des données officielles.',
      lien: '/publicationStrategie',
      icon: <DescriptionIcon />,
      color: '#008D36',
      readingTime: '6 min',
      tags: ['Stratégie', 'Communication', 'SSN', 'EAC']
    },
    {
      id: 'cafe-statistique-recent',
      title: 'Café Statistique - Couplage RGPH/RGAE',
      date: '18 Novembre 2025',
      category: 'Conférence',
      excerpt: 'Retours d\'expérience du RGPHAE 2024 et perspectives d\'innovation statistique',
      lien: '/cafeStatistiqueRGPH_RGAE',
      icon: <PublicIcon />,
      color: accentColor,
      readingTime: '5 min',
      tags: ['RGPHAE', 'Innovation', 'Technologie']
    },
    {
      id: 'formation-inclusion-recent',
      title: 'Formation sur l\'inclusion statistique',
      date: '16 Décembre 2025',
      category: 'Formation',
      excerpt: 'Renforcement des capacités des instituts nationaux de statistique sur l\'inclusion des populations déplacées',
      lien: '/formation-inclusion',
      icon: <LanguageIcon />,
      color: '#2E7D32',
      readingTime: '4 min',
      tags: ['Formation', 'Dakar', 'Cameroun']
    }
  ];

  // ---- GALERIE PHOTOS ----
  const galerieImages = [
    {
      id: 'strategie-1',
      title: 'Couverture de la Stratégie de Communication du SSN',
      date: '30 Juin 2026',
      image: '/assets/strategie-ssn/couverture-strategie.jpg',
      event: 'Publication officielle',
      category: 4
    },
    {
      id: 'validation-1',
      title: 'Ouverture de l\'atelier de validation',
      date: '08 mai 2026',
      image: '/assets/validation-atelier/image6presentation.jpeg',
      event: 'Discours de Mr NDAYISENGA Modeste',
      category: 4
    },
    {
      id: 'validation-2',
      title: 'Modérateur de l\'atelier',
      date: '08 mai 2026',
      image: '/assets/validation-atelier/image13.jpeg',
      event: 'Animation des travaux',
      category: 4
    },
    {
      id: 'validation-3',
      title: 'Participants - vue d\'ensemble',
      date: '08 mai 2026',
      image: '/assets/validation-atelier/image11.jpeg',
      event: 'Assistance nombreuse',
      category: 4
    },
    {
      id: 'validation-4',
      title: 'Échanges entre participants',
      date: '08 mai 2026',
      image: '/assets/validation-atelier/image14.jpeg',
      event: 'Contributions actives',
      category: 4
    },
    {
      id: 'validation-5',
      title: 'Photo de famille',
      date: '08 mai 2026',
      image: '/assets/validation-atelier/photodefamille4.jpeg',
      event: 'Clôture de l\'atelier',
      category: 4
    },
    {
      id: 1,
      title: 'Photo de famille - Clôture MSB 2025',
      date: '19 Novembre 2025',
      image: '/assets/msb/Cloture_JAS_19-11-2025_Royal_palace-photo_cloture.jpg',
      event: 'Cérémonie de clôture',
      category: 4
    },
    {
      id: 2,
      title: 'Discours du Premier Ministre - Clôture MSB 2025',
      date: '19 Novembre 2025',
      image: '/assets/msb/Cloture_JAS_19-11-2025_Royal_palace-ministre.jpg',
      event: 'Discours de circonstance',
      category: 4
    },
    {
      id: 3,
      title: 'Intervention du DG de l\'INSBU - Clôture MSB 2025',
      date: '19 Novembre 2025',
      image: '/assets/msb/Cloture_JAS_19-11-2025_Royal_palace-dg.jpg',
      event: 'Allocution d\'accueil',
      category: 4
    },
    {
      id: 4,
      title: 'Formation à Dakar - Inclusion statistique',
      date: '12 Décembre 2025',
      image: getImagePath("photo-dg_ins_burundi_cammeroune.png"),
      event: 'Formation internationale',
      category: 5
    },
    {
      id: 5,
      title: 'Photo de famille à l\'INS Cameroun',
      date: '16 Décembre 2025',
      image: getImagePath("photo_famille.png"),
      event: 'Voyage d\'étude',
      category: 5
    },
    {
      id: 6,
      title: 'Allocution de l\'UNFPA - Clôture MSB 2025',
      date: '19 Novembre 2025',
      image: '/assets/msb/Cloture_JAS_19-11-2025_Royal_palace-representUNFPA.jpg',
      event: 'Partenaires au développement',
      category: 4
    },
    {
      id: 7,
      title: 'Présentation de Dr. NTAGAHORAHO Thierry - Clôture MSB 2025',
      date: '19 Novembre 2025',
      image: '/assets/msb/Cloture_JAS_19-11-2025_Royal_palace_thiery-18.jpg',
      event: 'Facilitateur de la cérémonie',
      category: 4
    },
    {
      id: 8,
      title: 'Participants - Clôture MSB 2025',
      date: '19 Novembre 2025',
      image: '/assets/msb/public4.jpg',
      event: 'Participants à la cérémonie',
      category: 4
    }
  ];

  // ---- FILTRAGE ----
  const getFilteredEvents = () => {
    if (activeTab === 0) return [...evenementsPrincipaux, ...articlesRecents];
    return [...evenementsPrincipaux, ...articlesRecents].filter(event => event.category === activeTab);
  };

  // ---- GESTION DU SCROLL ----
  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ---- COMPOSANT IMAGE OPTIMISÉ ----
  const OptimizedImage = ({ src, alt, height = 200, objectFit = 'contain', priority = false, borderRadius = 1 }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
      if (priority && src) {
        const img = new Image();
        img.src = src;
        img.onload = () => setIsLoading(false);
        img.onerror = () => { setIsLoading(false); setHasError(true); };
      }
    }, [src, priority]);

    if (hasError) {
      return (
        <Box sx={{ height, width: '100%', bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius }}>
          <PhotoCameraIcon sx={{ fontSize: 40, color: '#ddd' }} />
        </Box>
      );
    }

    return (
      <Box sx={{ position: 'relative', height, width: '100%' }}>
        {isLoading && (
          <Skeleton variant="rectangular" width="100%" height={height} animation="wave" sx={{ position: 'absolute', top: 0, left: 0, borderRadius }} />
        )}
        <Box
          component="img"
          src={src}
          alt={alt}
          onLoad={() => setIsLoading(false)}
          onError={() => { setIsLoading(false); setHasError(true); }}
          sx={{
            width: '100%',
            height: '100%',
            objectFit,
            opacity: isLoading ? 0 : 1,
            transition: 'opacity 0.5s ease',
            borderRadius,
            filter: isLoading ? 'blur(10px)' : 'blur(0)'
          }}
          loading={priority ? "eager" : "lazy"}
        />
      </Box>
    );
  };

  // ---- NAVIGATION ET PARTAGE ----
  const handleNavigation = (path) => navigate(path);

  const handleShare = async (title, url) => {
    if (isSharing) return;
    const fullUrl = window.location.origin + url;
    if (navigator.share) {
      try {
        setIsSharing(true);
        await navigator.share({
          title: title,
          text: `Découvrez ${title} sur le site de l'INSBU`,
          url: fullUrl,
        });
      } catch (error) {
        if (error.name !== 'InvalidStateError' && error.name !== 'AbortError') {
          console.warn('Erreur de partage :', error);
        }
      } finally {
        setIsSharing(false);
      }
    } else {
      try {
        await navigator.clipboard.writeText(fullUrl);
        alert('Lien copié dans le presse-papier !');
      } catch (error) {
        console.warn('Erreur de copie :', error);
      }
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // ---- RENDU ----
  return (
    <Box sx={{ bgcolor: tertiaryColor, minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      <NavigationBar />
      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* HERO */}
        <Fade in timeout={1000}>
          <Box sx={{ textAlign: 'center', mb: 8, pt: 2 }}>
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <Typography variant={isMobile ? "h6" : "h5"} component="h5" gutterBottom sx={{ fontWeight: 700, color: secondaryColor, mb: 2 }}>
                Actualités & Événements
              </Typography>
              <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: '800px', mx: 'auto', mb: 4, lineHeight: 1.6, fontSize: { xs: '1rem', md: '1rem' } }}>
                Suivez en temps réel les activités, innovations et réalisations du <strong>Système Statistique National</strong> du Burundi
              </Typography>
            </motion.div>
          </Box>
        </Fade>

        {/* FILTRES */}
        <Box sx={{ mb: 6 }}>
          <Stack direction="row" sx={{ flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
            {categories.map((category) => (
              <Chip
                key={category.id}
                icon={category.icon}
                label={category.label}
                onClick={() => setActiveTab(category.id)}
                sx={{
                  bgcolor: activeTab === category.id ? alpha(primaryColor, 0.1) : 'white',
                  color: activeTab === category.id ? primaryColor : 'text.secondary',
                  border: `1px solid ${alpha(primaryColor, 0.1)}`,
                  '&:hover': { bgcolor: alpha(primaryColor, 0.05) }
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* ÉVÉNEMENTS MAJEURS - 4 colonnes sur lg */}
        <Box sx={{ mb: 10 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, position: 'relative', '&::after': { content: '""', position: 'absolute', bottom: -8, left: 0, width: '100px', height: '2px', bgcolor: neutralGray, borderRadius: 2 } }}>
            <Typography variant="h6" sx={{ color: neutralGray, fontWeight: 400 }}>
              Événements Majeurs
              <StarIcon sx={{ ml: 1, fontSize: 28, color: alpha(secondaryColor, 0.7), verticalAlign: 'middle' }} />
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', bgcolor: alpha(neutralGray, 0.05), px: 2, py: 0.5, borderRadius: 1 }}>
              Cliquez pour découvrir
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {getFilteredEvents()
              .filter(event => event.featured)
              .map((evenement, index) => (
                <Grid item xs={12} sm={6} md={6} lg={3} key={evenement.id}>
                  <Grow in timeout={500 + (index * 200)}>
                    <motion.div whileHover={{ y: -8 }} transition={{ type: "spring", stiffness: 300 }} style={{ height: '100%' }}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          boxShadow: theme.shadows[1],
                          overflow: 'hidden',
                          borderRadius: 2,
                          border: `1px solid ${alpha(evenement.color, 0.08)}`,
                          '&:hover': {
                            boxShadow: `0 12px 24px ${alpha(evenement.color, 0.08)}`,
                            '& .card-overlay': { opacity: 1 }
                          }
                        }}
                        onMouseEnter={() => setHoveredCard(evenement.id)}
                        onMouseLeave={() => setHoveredCard(null)}
                      >
                        <CardActionArea
                          onClick={() => handleNavigation(evenement.lien)}
                          sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                        >
                          {/* IMAGE */}
                          <Box sx={{ position: 'relative', height: 200, flexShrink: 0 }}>
                            <OptimizedImage
                              src={evenement.image}
                              alt={evenement.title}
                              height={200}
                              objectFit="contain"
                              priority={index === 0}
                            />
                            <Box
                              className="card-overlay"
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                bgcolor: alpha('#000', 0.6),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 0,
                                transition: 'opacity 0.3s ease'
                              }}
                            >
                              <Button variant="contained" sx={{ bgcolor: 'white', color: evenement.color, '&:hover': { bgcolor: '#f5f5f5' } }}>
                                Voir les détails
                              </Button>
                            </Box>
                            <Chip
                              label={evenement.badge}
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 16,
                                left: 16,
                                bgcolor: evenement.color,
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                bgcolor: 'rgba(0,0,0,0.7)',
                                color: 'white',
                                p: 1,
                                display: 'flex',
                                justifyContent: 'space-around',
                                fontSize: '0.7rem'
                              }}
                            >
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" sx={{ opacity: 0.8 }}>Participants</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{evenement.stats.participants}</Typography>
                              </Box>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="caption" sx={{ opacity: 0.8 }}>Durée</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{evenement.stats.duration}</Typography>
                              </Box>
                            </Box>
                          </Box>

                          {/* CONTENU */}
                          <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                              <Avatar sx={{ bgcolor: alpha(evenement.color, 0.08), color: evenement.color, width: 40, height: 40, mr: 1.5, flexShrink: 0 }}>
                                {evenement.icon}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '0.95rem', mb: 0.3 }}>
                                  {evenement.title}
                                </Typography>
                                <Typography variant="caption" sx={{ color: evenement.color, fontWeight: 600, display: 'block' }}>
                                  {evenement.subtitle}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', mt: 0.3 }}>
                                  <CalendarIcon sx={{ fontSize: 12, mr: 0.5 }} />
                                  {evenement.date}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                flex: 1,
                                lineHeight: 1.5,
                                fontSize: '0.8rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                mb: 1.5
                              }}
                            >
                              {evenement.description}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', color: evenement.color }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, mr: 0.5 }}>Consulter</Typography>
                                <ArrowForwardIcon sx={{ fontSize: 16, transition: 'transform 0.3s', transform: hoveredCard === evenement.id ? 'translateX(4px)' : 'none' }} />
                              </Box>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShare(evenement.title, evenement.lien);
                                }}
                                disabled={isSharing}
                                sx={{ color: 'text.secondary' }}
                              >
                                <ShareIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </motion.div>
                  </Grow>
                </Grid>
              ))}
          </Grid>
        </Box>

        {/* SECTION PROGRAMME COMPLET */}
        <Zoom in timeout={800}>
          <Paper sx={{ p: { xs: 3, md: 4 }, mb: 8, bgcolor: 'white', border: `1px solid ${alpha(neutralGray, 0.12)}`, borderRadius: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.06)' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                  <Avatar sx={{ bgcolor: alpha(neutralGray, 0.08), color: neutralGray, width: 56, height: 56, mr: 2.5 }}>
                    <CalendarIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 400, color: neutralGray, mb: 0.6 }}>Programme Complet MSB 2025</Typography>
                    <Typography variant="h6" sx={{ color: secondaryColor, fontWeight: 400 }}>Du 20 Octobre au 20 Novembre 2025</Typography>
                  </Box>
                </Box>
                <Typography variant="body1" paragraph sx={{ mb: 2.5, lineHeight: 1.7, fontSize: '1rem' }}>
                  Découvrez l'ensemble des activités planifiées pour cette 14ème édition, incluant les formations, ateliers, célébrations et productions statistiques.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button onClick={() => handleNavigation('/moisStatistique2025')} variant="contained" startIcon={<NewspaperIcon />} sx={{ bgcolor: neutralGray, '&:hover': { bgcolor: '#616161' }, px: 3, py: 1.2, fontWeight: 600 }}>Programme détaillé</Button>
                  <Button onClick={() => handleNavigation('/lancementMoisStatistique2025')} variant="outlined" startIcon={<LaunchIcon />} sx={{ borderColor: alpha(primaryColor, 0.3), color: primaryColor, px: 3, py: 1.2, fontWeight: 600 }}>Lancement</Button>
                  <Button onClick={() => handleNavigation('/formation-inclusion')} variant="outlined" startIcon={<LanguageIcon />} sx={{ borderColor: alpha(secondaryColor, 0.3), color: secondaryColor, px: 3, py: 1.2, fontWeight: 600 }}>Formations</Button>
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ bgcolor: alpha(neutralGray, 0.03), p: 2.5, borderRadius: 1.5, border: `1px solid ${alpha(neutralGray, 0.1)}` }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: neutralGray, mb: 2 }}>En chiffres</Typography>
                  <Stack spacing={2}>
                    <Box><Typography variant="body2" sx={{ color: 'text.secondary' }}>Durée</Typography><Typography variant="h6" sx={{ fontWeight: 700 }}>1 mois</Typography></Box>
                    <Box><Typography variant="body2" sx={{ color: 'text.secondary' }}>Activités</Typography><Typography variant="h6" sx={{ fontWeight: 700 }}>20+ événements</Typography></Box>
                    <Box><Typography variant="body2" sx={{ color: 'text.secondary' }}>Partenaires</Typography><Typography variant="h6" sx={{ fontWeight: 700 }}>15+ institutions</Typography></Box>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Zoom>

        {/* GALERIE PHOTOS */}
        <Box sx={{ mb: 8 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, position: 'relative', '&::after': { content: '""', position: 'absolute', bottom: -8, left: 0, width: '100px', height: '2px', bgcolor: accentColor, borderRadius: 2 } }}>
            <Typography variant="h4" sx={{ color: neutralGray, fontWeight: 700 }}>Galerie Photos</Typography>
            <Typography variant="caption" color="text.secondary">Moments forts des événements</Typography>
          </Box>
          <Grid container spacing={2}>
            {galerieImages.map((photo, index) => (
              <Grid item xs={12} sm={6} md={3} key={photo.id}>
                <Grow in timeout={200 + (index * 100)}>
                  <Card sx={{ height: '100%', transition: 'all 0.3s ease', overflow: 'hidden', borderRadius: 1.5, boxShadow: theme.shadows[1], '&:hover': { boxShadow: theme.shadows[3], '& .photo-overlay': { opacity: 1 }, '& .photo-title': { transform: 'translateY(0)' } } }}>
                    <CardActionArea>
                      <Box sx={{ position: 'relative', height: 220 }}>
                        <OptimizedImage src={photo.image} alt={photo.title} height={220} objectFit="contain" />
                        <Box className="photo-overlay" sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(0,0,0,0.6)', opacity: 0, transition: 'opacity 0.3s ease', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', p: 2 }}>
                          <Typography className="photo-title" variant="body1" sx={{ color: 'white', fontWeight: 600, mb: 1, transform: 'translateY(20px)', transition: 'transform 0.3s ease', fontSize: '0.95rem' }}>{photo.title}</Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>{photo.event}</Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5, fontSize: '0.7rem' }}>{photo.date}</Typography>
                        </Box>
                      </Box>
                    </CardActionArea>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      {showScrollTop && (
        <Fab color="primary" aria-label="scroll back to top" onClick={scrollToTop} sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000, bgcolor: alpha(neutralGray, 0.9), color: 'white', '&:hover': { bgcolor: neutralGray } }}>
          <TrendingIcon />
        </Fab>
      )}
      <Footer />
    </Box>
  );
}

export default ActualitesEtEvenements;